import type {
  ICreatePiiCustomEntityParams,
  IDeletePiiCustomEntityParams,
  IGetPiiSettingsParams,
  IListPiiCustomEntitiesParams,
  IListPiiCustomEntityLabelsParams,
  IPiiCustomEntity,
  IPiiCustomEntityLabel,
  IPiiSettings,
  IUpdatePiiCustomEntityParams,
  IUpdatePiiSettingsParams,
} from "../types/pii";
import type { ApiClient } from "./client";

/**
 * NOTE: NSAI-5941 / NSAI-5942 / NSAI-5946 — the organization-scoped PII
 * configuration API, lifted out of `apps/nsai/web/src/api/pii/` so the mobile
 * sections consume the same contract rather than a second transcription of it.
 *
 * The endpoint families sit behind one class because they are one feature to
 * the user (the Chat Settings PII block) and share the org-uuid path prefix:
 *
 * - `/pii-settings` — the entity allowlist and confidence threshold, behind
 *   IS_PII_ADVANCED_RULES_FEATURE_ENABLED (`isPiiAdvancedRulesFeatureEnabled`).
 * - `/pii-custom-entities` and `/pii-custom-entity-labels` — org-defined
 *   regex / dictionary detectors and their display names, behind
 *   IS_PII_CUSTOM_ENTITIES_FEATURE_ENABLED (`isPiiCustomEntitiesFeatureEnabled`).
 *
 * NOTE: Each family 404s (not 403) when its flag is off, so the feature is
 * indistinguishable from "not implemented". Callers must therefore gate on the
 * app-config flag rather than probing the endpoint and reading a 404 as "no
 * data". NSAI-6787 removed `/pii-test`; the Anonymization Lab replaces it.
 */
export class PiiApi {
  constructor(private readonly apiClient: ApiClient) {}

  async getPiiSettings({
    organizationUuid,
  }: IGetPiiSettingsParams): Promise<IPiiSettings> {
    const response = await this.apiClient.get<IPiiSettings>(
      `/organizations/${organizationUuid}/pii-settings`,
    );
    return response.data;
  }

  // NOTE: PUT, not PATCH — the route is a PUT that applies patch semantics
  // itself (undefined preserves, null clears the override). Sending PATCH
  // would 404.
  async updatePiiSettings({
    organizationUuid,
    body,
  }: IUpdatePiiSettingsParams): Promise<IPiiSettings> {
    const response = await this.apiClient.put<IPiiSettings>(
      `/organizations/${organizationUuid}/pii-settings`,
      body,
    );
    return response.data;
  }

  async listCustomEntities({
    organizationUuid,
  }: IListPiiCustomEntitiesParams): Promise<IPiiCustomEntity[]> {
    const response = await this.apiClient.get<IPiiCustomEntity[]>(
      `/organizations/${organizationUuid}/pii-custom-entities`,
    );
    return response.data;
  }

  async createCustomEntity({
    organizationUuid,
    body,
  }: ICreatePiiCustomEntityParams): Promise<IPiiCustomEntity> {
    const response = await this.apiClient.post<IPiiCustomEntity>(
      `/organizations/${organizationUuid}/pii-custom-entities`,
      body,
    );
    return response.data;
  }

  // NOTE: A full-body PUT: the server re-validates the entity as a whole, so a
  // caller flipping only `enabled` still sends every other field unchanged.
  async updateCustomEntity({
    organizationUuid,
    entityUuid,
    body,
  }: IUpdatePiiCustomEntityParams): Promise<IPiiCustomEntity> {
    const response = await this.apiClient.put<IPiiCustomEntity>(
      `/organizations/${organizationUuid}/pii-custom-entities/${entityUuid}`,
      body,
    );
    return response.data;
  }

  async deleteCustomEntity({
    organizationUuid,
    entityUuid,
  }: IDeletePiiCustomEntityParams): Promise<void> {
    await this.apiClient.delete(
      `/organizations/${organizationUuid}/pii-custom-entities/${entityUuid}`,
    );
  }

  async listCustomEntityLabels({
    organizationUuid,
  }: IListPiiCustomEntityLabelsParams): Promise<IPiiCustomEntityLabel[]> {
    const response = await this.apiClient.get<IPiiCustomEntityLabel[]>(
      `/organizations/${organizationUuid}/pii-custom-entity-labels`,
    );
    return response.data;
  }
}
